import { useEffect, useState, useRef, useMemo, useContext } from "react";
import Box from "@mui/material/Box";
import Fade from "@mui/material/Fade";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Drawer from "@mui/material/Drawer";
import Typography from "@mui/material/Typography";
import { styled } from "@mui/material/styles";
import useTheme from "@mui/material/styles/useTheme";
import useMediaQuery from "@mui/material/useMediaQuery";

import { APP_STATES, ROOT_DIV_ID, STREAM_TYPES } from "shared/enum";

import { AppContext } from "contexts/App";
import { DataContext } from "contexts/Data";
import { StreamContext } from "contexts/Stream";
import useVariant from "hooks/useVariant";
import usePreventZoom from "hooks/usePreventZoom";
import useGtagVariantData from "hooks/useGtagVariantData";
import TopBar from "components/TopBar";
import BookNow from "components/BookNow";
import FeaturesTab from "./FeaturesTab";
import RightPanel from "components/RightPanel";
import ModelSelector from "components/ModelSelector";
import BottomBar from "components/BottomBar";
import StreamingAvailablePopup from "components/streaming/StreamingAvailablePopup";
import StreamingNotAvailablePopup from "components/streaming/StreamingNotAvailablePopup";
import StreamLoadingScreen from "components/StreamLoadingScreen";
import FeaturePreview from "components/FeaturePreview";
import ConfigurationsTab from "./ConfigurationsTab";
import StreamRoot from "components/streaming/StreamRoot";
import Turntable from "components/visualization2D/Turntable";
import { filterAccessories, countAccessories } from "helpers/variant";
import { isDarkTheme } from "helpers/darkTheme";

import type { FeatureListItem } from "types/app";
import type { TypographyProps } from "@mui/material";
import useTimer from "hooks/useTimer";

const homeRootElement = document.getElementById(ROOT_DIV_ID)!; // eslint-disable-line @typescript-eslint/no-non-null-assertion

type FeaturePreviewOptions = {
  open: boolean;
  title: string;
  imgPath?: string;
  videoPath?: string;
};

type SelectedTab = "configuration" | "feature";

const TABS = {
  CONFIGURATION: "configuration" as SelectedTab,
  FEATURE: "feature" as SelectedTab,
} as const;

export function DrawerHeader() {
  return (
    <DrawerHeaderContainer mt={1} py={2} px={3} top={0} position="sticky" direction="column">
      <div style={{ display: "flex", justifyContent: "space-between" }}>
        <ModelSelector />
      </div>
    </DrawerHeaderContainer>
  );
}

type TabSelectorProps = {
  selectedTab: SelectedTab;
  setSelectedTab: (tab: SelectedTab) => void;
};

function TabSelector({ selectedTab, setSelectedTab }: TabSelectorProps) {
  return (
    <TabSelectorWrapper
      direction="row"
      justifyContent="center"
      columnGap={1}
      pt={2}
      px={3}
      pb={0.25}
      mb={2}
    >
      <TabTitle
        variant="body1"
        selected={selectedTab === TABS.CONFIGURATION}
        onClick={() => setSelectedTab(TABS.CONFIGURATION)}
      >
        Variants & Packs
      </TabTitle>

      {/* <TabTitle
        variant="body1"
        selected={selectedTab === TABS.FEATURE}
        onClick={() => setSelectedTab(TABS.FEATURE)}
      >
        Features
      </TabTitle> */}
    </TabSelectorWrapper>
  );
}

type ContentRootProps = {
  header: null | JSX.Element;
  price: number;
  isMobile: boolean;
  handleSaveClick: () => void;
  handleShareClick: () => void;
  handleBookNowClick: () => void;
  openFeaturePreview: (previewOptions: FeatureListItem, disableAnalyticsEvent?: boolean) => void;
  closeFeaturePreview: () => void;
};

function ContentRoot({
  header,
  price,
  isMobile,
  handleSaveClick,
  handleShareClick,
  handleBookNowClick,
  openFeaturePreview,
  closeFeaturePreview,
}: ContentRootProps) {
  usePreventZoom(true, false);
  const [selectedTab, setSelectedTab] = useState<SelectedTab>(TABS.CONFIGURATION);

  return (
    <>
      <PanelContainer>
        {header}

        <Box>
          <TabSelector selectedTab={selectedTab} setSelectedTab={setSelectedTab} />

          {selectedTab === TABS.CONFIGURATION && (
            <Fade in={true}>
              <TabContainer>
                <ConfigurationsTab
                  openFeaturePreview={openFeaturePreview}
                  closeFeaturePreview={closeFeaturePreview}
                />
              </TabContainer>
            </Fade>
          )}

          {/* {selectedTab === TABS.FEATURE && (
            <Fade in={true}>
              <TabContainer>
                <FeaturesTab openFeaturePreview={openFeaturePreview} />
              </TabContainer>
            </Fade>
          )} */}
        </Box>
      </PanelContainer>

      {/* <BookNowRoot>
        <BookNow
          price={price}
          isMobile={isMobile}
          handleSaveClick={handleSaveClick}
          handleShareClick={handleShareClick}
          handleBookNowClick={handleBookNowClick}
        />
      </BookNowRoot> */}
    </>
  );
}

export default function Home() {
  const theme = useTheme();
  const { appLoadingState } = useContext(AppContext);
  const { accessories, selectedAccessories } = useContext(DataContext);
  const { cameras, scenarios, streamingState, turntableState, streamViewType } =
    useContext(StreamContext);

  const { switchStream, isStreamingAvailable, streamingNotAvailable, callStreamingAvailableAPI } =
    streamingState;
  const { isTurntable, turntableIsEnabled } = turntableState;

  const [drawerOpen, setDrawerOpen] = useState<boolean>(true);
  const [isFullscreen, setIsFullscreen] = useState<boolean>(false);
  const [shouldUseInitalConfig] = useState(true);
  const [featurePreviewOptions, setFeaturePreviewOptions] = useState<FeaturePreviewOptions>({
    open: false,
    title: "",
    imgPath: "",
    videoPath: "",
  });

  // loading screen for streaming
  const [loadingScreen, setLoadingScreen] = useState(false);

  const lastNumSelectedAccessories = useRef(0);
  const topBarRef = useRef<HTMLDivElement>(null);
  const rightPanelRef = useRef<HTMLDivElement>(null);

  const { time, stopTimer, startTime } = useTimer(0);

  const isTab = useMediaQuery(theme.breakpoints.between("md", "lg"));
  const drawerWidth = useMediaQuery(theme.breakpoints.down("md")) ? 250 : isTab ? 320 : 360;

  const variant = useVariant();
  const getGtagData = useGtagVariantData();

  const accessoriesPrice = useMemo(() => {
    return filterAccessories(selectedAccessories, accessories).reduce(
      (acc, { price }) => acc + price,
      0,
    );
  }, [accessories, selectedAccessories]);

  const totalPrice = accessoriesPrice + (variant?.price || 0);
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  /**
   * Auto close side-drawer if in `in-glo` stream mode
   */
  if (streamViewType === STREAM_TYPES.IN_GLO && drawerOpen) {
    setDrawerOpen(false);
  }

  const toggleFullscreen = async () => {
    if (document.fullscreenElement) {
      document.exitFullscreen();
    } else {
      homeRootElement.requestFullscreen().catch(console.error);
    }
  };

  const openDrawer = () => {
    if (streamViewType === STREAM_TYPES.IN_GLO) return;

    setDrawerOpen(true);
  };

  const closeDrawer = () => setDrawerOpen(false);

  const openFeaturePreview = (previewOptions: FeatureListItem, disableAnalyticsEvent = false) => {
    if (isMobile) {
      setDrawerOpen(false);
    }

    if (!disableAnalyticsEvent) {
      window.gtag("event", "feature_preview_open", getGtagData("engagement", previewOptions.title));
    }

    setFeaturePreviewOptions({
      open: true,
      title: previewOptions.title,
      imgPath: previewOptions.previewImg,
      videoPath: previewOptions.previewVideo,
    });
  };

  const closeFeaturePreview = () => {
    if (isMobile) {
      setDrawerOpen(true);
    }

    setFeaturePreviewOptions({ ...featurePreviewOptions, open: false });
  };

  const handleSaveClick = () => {};
  const handleShareClick = () => {};

  const handleBookNowClick = () => {
    window.gtag("event", "book_now_click", getGtagData("engagement", "Book Now"));

    window.open("https://www.mahindraelectricsuv.com/", "_blank");
  };

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    document.addEventListener("fullscreenchange", handleFullscreenChange);

    return () => {
      // resizeObserver.disconnect();
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
    };
  }, []);

  useEffect(() => {
    const numSelectedAccessories = countAccessories(selectedAccessories);

    /**
     * Close open previews if the user deselects an accessory (any) while the preview is open
     */
    if (numSelectedAccessories < lastNumSelectedAccessories.current && featurePreviewOptions.open) {
      closeFeaturePreview();
    }

    lastNumSelectedAccessories.current = numSelectedAccessories;
  }, [selectedAccessories]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    document.addEventListener("fullscreenchange", handleFullscreenChange);

    return () => {
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
    };
  }, []);

  useEffect(() => {
    if (isMobile) {
      return closeFeaturePreview();
    }
  }, [isMobile]); // eslint-disable-line react-hooks/exhaustive-deps

  const [streamResize, setStreamResize] = useState<VoidFunction>();

  useEffect(() => {
    streamResize && streamResize();
  }, [drawerOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (featurePreviewOptions.open) {
      setFeaturePreviewOptions({ ...featurePreviewOptions, open: false });
    }
  }, [variant?.code]); // eslint-disable-line react-hooks/exhaustive-deps

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleStream = (shouldStream: boolean) => {
    // setDemoState((prev) => ({
    //   ...prev,
    //   isDoorsOpen: false,
    // }));
    // setStreamingDisconnected(false);
    // setCachedState(DEFAULT_CACHED_STATE);
    // setStreamStarted(shouldStream);
  };

  const handleLoadScreen = (val: boolean, delay = 1000) => {
    if (!val) {
      setTimeout(() => {
        setLoadingScreen(val);
      }, delay);
      return;
    }
    setLoadingScreen(val);
  };
  const isIngloPlatform = streamViewType === STREAM_TYPES.IN_GLO;

  return (
    <div style={{ height: "100%", display: "flex", flexDirection: "column" }}>
      <StreamLoadingScreen isLoading={loadingScreen} />
      <StreamingSceneWrapper drawerOpen={isMobile ? false : drawerOpen} drawerWidth={drawerWidth}>
        {isTurntable && cameras?.length > 0 && scenarios?.length > 0 ? (
          <Turntable
            isMobile={isMobile}
            turntableIsEnabled={!drawerOpen && turntableIsEnabled}
            drawerOpen={drawerOpen}
            closeDrawer={closeDrawer}
            handleLoadScreen={handleLoadScreen}
          />
        ) : (
          <>
            {(streamViewType === STREAM_TYPES.STREAMING ||
              streamViewType === STREAM_TYPES.IN_GLO) &&
              !switchStream &&
              appLoadingState === APP_STATES.LOADED && (
                <StreamRoot
                  shouldUseInitalConfig={shouldUseInitalConfig}
                  setResize={setStreamResize}
                  isMobile={isMobile}
                  turntableIsEnabled={!drawerOpen && turntableIsEnabled}
                  drawerOpen={drawerOpen}
                  sidebarMouseplayTime={time}
                  handleLoadScreen={handleLoadScreen}
                />
              )}

            {/* {!isStreamStarted && isStreamDisconnected && !switchStream && (
              <DisconnectedPopup
                handleRestart={() => {
                  // if (restartStreamFn) {
                  //   restartStreamFn();
                  // }
                  // setStreamingDisconnected(false);

                  setStreamingState({
                    ...streamingState,
                    isStreamStarted: true,
                    isStreamDisconnected: false,
                  });
                }}
              />
            )} */}
          </>
        )}

        {featurePreviewOptions.open && (
          <FeaturePreview options={featurePreviewOptions} handleClose={closeFeaturePreview} />
        )}

        {appLoadingState === APP_STATES.LOADED && (
          <>
            <TopBar
              topBarRef={topBarRef}
              isMobile={isMobile}
              drawerOpen={drawerOpen}
              isFullscreen={isFullscreen}
              featurePreviewOpen={featurePreviewOptions.open}
              openMenu={openDrawer}
              closeMenu={closeDrawer}
              toggleFullscreen={toggleFullscreen}
              closeFeaturePreview={closeFeaturePreview}
              disableDrawerOpen={streamViewType === STREAM_TYPES.IN_GLO}
            />

            {!isIngloPlatform && (
              <RightPanel
                topBarRef={topBarRef}
                rightPanelRef={rightPanelRef}
                isMobile={isMobile}
                handleSaveClick={handleSaveClick}
                handleShareClick={handleShareClick}
                handleLoadScreen={handleLoadScreen}
                drawerOpen={drawerOpen}
              />
            )}

            <BottomBar
              isMobile={isMobile}
              drawerOpen={drawerOpen}
              closeDrawer={closeDrawer}
              handleStream={handleStream}
              handleLoadScreen={handleLoadScreen}
            />
          </>
        )}
      </StreamingSceneWrapper>

      {isStreamingAvailable && isTurntable && callStreamingAvailableAPI && (
        <StreamingAvailablePopup handleStream={handleStream} />
      )}

      {streamingNotAvailable && <StreamingNotAvailablePopup />}

      {!isMobile && appLoadingState === APP_STATES.LOADED && (
        <Drawer
          variant="persistent"
          anchor="right"
          open={drawerOpen}
          onClose={() => {}}
          PaperProps={{
            sx: {
              background: "transparent",
              backgroundImage: "unset",
            },
          }}
        >
          <DrawerContainer
            drawerWidth={drawerWidth}
            onMouseEnter={() => startTime()}
            onMouseLeave={() => stopTimer()}
          >
            <ContentRoot
              header={<DrawerHeader />}
              price={totalPrice}
              isMobile={isMobile}
              handleSaveClick={handleSaveClick}
              handleShareClick={handleShareClick}
              handleBookNowClick={handleBookNowClick}
              openFeaturePreview={openFeaturePreview}
              closeFeaturePreview={closeFeaturePreview}
            />
          </DrawerContainer>
        </Drawer>
      )}

      {isMobile && (
        <MobileContentWrapper>
          <ContentRoot
            header={null}
            price={totalPrice}
            isMobile={isMobile}
            handleSaveClick={handleSaveClick}
            handleShareClick={handleShareClick}
            handleBookNowClick={handleBookNowClick}
            openFeaturePreview={openFeaturePreview}
            closeFeaturePreview={closeFeaturePreview}
          />
        </MobileContentWrapper>
      )}
    </div>
  );
}
type DrawerContainerProps = {
  drawerWidth: number;
};
const DrawerContainer = styled(Paper)<DrawerContainerProps>`
  width: ${(props) => props.drawerWidth}px;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  backdrop-filter: blur(5px);
  background-image: unset;
  border-left: 1px solid
    ${({ theme }) =>
      isDarkTheme(theme.palette.mode) ? "rgba(173, 188, 193, 0.6)" : "rgba(0, 0, 0, 0.6)"};

  ${({ theme }) => theme.breakpoints.down("sm")} {
    width: 100vw;
  }
`;

const TabContainer = styled("div")``;

const DrawerHeaderContainer = styled(Stack)`
  backdrop-filter: blur(8px);
  z-index: 2;
`;

type StreamingSceneWrapperProps = {
  drawerOpen: boolean;
  drawerWidth: number;
};

const StreamingSceneWrapper = styled("main")<StreamingSceneWrapperProps>`
  width: calc(100% - ${(props) => (props.drawerOpen ? props.drawerWidth + "px" : "0px")});
  height: 100%;
  position: relative;
  overflow: hidden;
  transition: width 0.3s;
  z-index: ${({ theme }) => theme.zIndex.drawer};
  background-color: ${({ theme }) => (isDarkTheme(theme.palette.mode) ? "#000" : "#fff")};

  ${({ theme }) => theme.breakpoints.down("sm")} {
    width: 100%;
    height: 100%;
  }
`;

const MobileContentWrapper = styled("div")`
  overflow-y: scroll;

  ${({ theme }) => theme.breakpoints.down("sm")} {
    width: 100%;
    height: 400px;
  }
`;

const TabSelectorWrapper = styled(Stack)`
  border-bottom: 1px solid #d63d30;
  background-color: ${({ theme }) => theme.palette.background.default};
  z-index: 2;

  ${({ theme }) => theme.breakpoints.down("sm")} {
    position: sticky;
    top: 0;
  }
`;

type TabTitleProps = TypographyProps & {
  selected?: boolean;
};

const TabTitle = styled(Typography, {
  shouldForwardProp: (prop) => prop !== "selected",
})<TabTitleProps>(
  ({ selected, theme }) => `
  font-optical-sizing: auto;
  font-style: normal;
  font-variation-settings: "wdth" 112.5;
  font-size: 0.9rem;
  cursor: pointer;
  color: ${isDarkTheme(theme.palette.mode) ? (selected ? "#fff" : theme.palette.secondary.main) : selected ? "#000" : "rgba(0, 0, 0, 0.5)"};
  text-transform: capitalize;
  position: relative;
  transition: font-weight 0.2s;
  user-select: none;
  background-color: rgba(0, 0, 0, 0.4);
  padding: 0.4rem 0.9rem;
  border-radius: 1px;
  border-style: solid;
  border-color: transparent;
  transition: color 0.3s ease;
  text-align: center;
  width: 100%;

  &:after {
    content: "";
    height: ${selected ? "2px" : "0"};
    // greater than 100% to cover the padding of the whole box
    width: 103%;
    background-color: #D63D30;
    position: absolute;
    border-radius: 2px;
    bottom: -0.35em;
    left: 50%;
    transition: height 0.3s;
    transform: translate(-50%);
  }
`,
);

const PanelContainer = styled("div")`
  height: 100%;
  overflow-y: auto;
  overflow-x: hidden;

  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-thumb {
    box-shadow: inset 0 0 6px rgba(0, 0, 0, 0.3);
    background-color: #a9a9a9;
    border-radius: 100px;
  }

  &::-webkit-scrollbar-track {
    background-color: #c0c0c0;
    border-radius: 100px;
  }

  ${({ theme }) => theme.breakpoints.down("sm")} {
    // static margin to prevent the <BookNowContainer /> cover up the bottom
    margin-bottom: 90px;
  }
`;

const BookNowRoot = styled("div")`
  padding: 0.5rem 1rem 1rem 1rem;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;

  ${({ theme }) => theme.breakpoints.down("sm")} {
    flex-direction: row;
    padding: 0;
    position: absolute;
    bottom: 0;
  }
`;
